import React, { useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../services/api';
import { useAuth } from '../../store/AuthContext';
import { 
  Rocket, 
  X, 
  Loader2,
  Globe,
  Tag,
  Briefcase
} from 'lucide-react';

const LaunchMissionModal = ({ isOpen, onClose, onLaunch }) => {
  const { user, token, refreshProfile } = useAuth();
  const [formData, setFormData] = useState({
    brand: user?.brand || '',
    market: user?.market || '',
    country: user?.country || 'España'
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResults(null);
    try {
      const response = await axios.post(`${getApiUrl()}/ai/analyze-market`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(response.data.data);
      if (refreshProfile) await refreshProfile();
      if (onLaunch) onLaunch(response.data.data);
    } catch (err) {
      alert('Error launching mission: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#b2bec3', marginBottom: '8px' }}>
          <Tag size={14} /> MAIN BRAND
        </label>
        <input 
          type="text"
          value={formData.brand}
          onChange={(e) => setFormData({...formData, brand: e.target.value})}
          placeholder="e.g. Carrefour, Starbucks..."
          style={{
            width: '100%',
            backgroundColor: '#1A1A1A',
            border: '1px solid #2d3436',
            padding: '12px',
            color: 'white',
            borderRadius: '4px',
            outline: 'none',
            fontSize: '14px'
          }}
          required
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#b2bec3', marginBottom: '8px' }}>
          <Briefcase size={14} /> SECTOR / MARKET
        </label>
        <input 
          type="text"
          value={formData.market}
          onChange={(e) => setFormData({...formData, market: e.target.value})}
          placeholder="e.g. Supermarkets, Coffee Shops..."
          style={{
            width: '100%',
            backgroundColor: '#1A1A1A',
            border: '1px solid #2d3436',
            padding: '12px',
            color: 'white',
            borderRadius: '4px',
            outline: 'none',
            fontSize: '14px'
          }}
          required
        />
      </div>

      <div style={{ marginBottom: '30px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#b2bec3', marginBottom: '8px' }}>
          <Globe size={14} /> COUNTRY
        </label>
        <input 
          type="text"
          value={formData.country}
          onChange={(e) => setFormData({...formData, country: e.target.value})}
          placeholder="e.g. España, Mexico, USA..."
          style={{
            width: '100%',
            backgroundColor: '#1A1A1A',
            border: '1px solid #2d3436',
            padding: '12px',
            color: 'white',
            borderRadius: '4px',
            outline: 'none',
            fontSize: '14px'
          }}
          required
        />
      </div>

      <div style={{
        backgroundColor: 'rgba(0, 206, 201, 0.05)',
        borderLeft: '2px solid #00CEC9',
        padding: '12px',
        marginBottom: '25px',
        fontSize: '11px',
        color: '#b2bec3',
        lineHeight: '1.5'
      }}>
        La IA del sistema analizará qué negocios pueden ser competencia en el país indicado y cuáles pueden ser un motor para su desarrollo. 
        <strong> Comprueba que el nombre es correcto</strong>, teniendo en cuenta que el análisis de mapas se basará estrictamente en dicho nombre.
      </div>

      <button 
        type="submit"
        disabled={loading}
        style={{
          width: '100%',
          backgroundColor: '#0B3D91',
          color: 'white',
          border: 'none',
          padding: '15px',
          borderRadius: '4px',
          fontFamily: "'Orbitron', sans-serif",
          letterSpacing: '2px',
          cursor: loading ? 'not-allowed' : 'pointer',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 15px rgba(11, 61, 145, 0.3)'
        }}
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin" size={20} />
            ANALYZING GALAXY...
          </>
        ) : (
          <>
            <Rocket size={20} />
            INICIAR ANALISIS IA
          </>
        )}
      </button>
    </form>
  );

  const renderResults = () => (
    <div style={{ textAlign: 'left' }}>
      <div style={{ 
        backgroundColor: 'rgba(0, 206, 201, 0.1)', 
        border: '1px solid #00CEC9', 
        padding: '15px', 
        borderRadius: '4px',
        marginBottom: '20px'
      }}>
        <h4 style={{ color: '#00CEC9', margin: '0 0 10px 0', fontSize: '12px', letterSpacing: '1px' }}>ANALYSIS COMPLETE</h4>
        <p style={{ fontSize: '11px', color: '#b2bec3', margin: 0 }}>The AI has identified the following strategic brands for the mission.</p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h5 style={{ color: '#E27B58', fontSize: '10px', letterSpacing: '1px', marginBottom: '8px' }}>DIRECT COMPETENCE</h5>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          {results.competence.map((c, i) => (
            <span key={i} style={{ backgroundColor: '#1A1A1A', color: 'white', padding: '4px 8px', borderRadius: '3px', fontSize: '10px', border: '1px solid #2d3436' }}>
              {c}
            </span>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <h5 style={{ color: '#00CEC9', fontSize: '10px', letterSpacing: '1px', marginBottom: '8px' }}>MARKET ENGINES</h5>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          {results.engines.map((e, i) => (
            <span key={i} style={{ backgroundColor: '#1A1A1A', color: 'white', padding: '4px 8px', borderRadius: '3px', fontSize: '10px', border: '1px solid #2d3436' }}>
              {e}
            </span>
          ))}
        </div>
      </div>

      <button 
        onClick={onClose}
        style={{
          width: '100%',
          backgroundColor: '#E27B58',
          color: 'white',
          border: 'none',
          padding: '12px',
          borderRadius: '4px',
          fontFamily: "'Orbitron', sans-serif",
          letterSpacing: '2px',
          cursor: 'pointer'
        }}
      >
        CLOSE & VIEW ON MAP
      </button>
    </div>
  );

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(5, 7, 10, 0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '16px',
      overflowY: 'auto',
      zIndex: 2500,
      backdropFilter: 'blur(4px)'
    }}>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-spin {
            animation: spin 1s linear infinite;
          }
        `}
      </style>
      <div style={{
        backgroundColor: '#0A0A0A',
        border: '2px solid #0B3D91',
        padding: '40px',
        width: '100%',
        maxWidth: '450px',
        maxHeight: 'calc(100dvh - 32px)',
        overflowY: 'auto',
        borderRadius: '8px',
        boxShadow: '0 0 50px rgba(11, 61, 145, 0.5)',
        position: 'relative'
      }}>
        <button onClick={onClose} style={{
          position: 'absolute',
          right: '20px',
          top: '20px',
          background: 'none',
          border: 'none',
          color: '#636e72',
          cursor: 'pointer'
        }}>
          <X size={24} />
        </button>

        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <Rocket size={48} color="#E27B58" style={{ marginBottom: '15px' }} />
          <h2 style={{ 
            fontFamily: "'Orbitron', sans-serif", 
            letterSpacing: '3px',
            color: 'white',
            margin: 0
          }}>{results ? 'MISSION READY' : 'MISSION CONTROL'}</h2>
          <p style={{ color: '#00CEC9', fontSize: '12px', marginTop: '5px' }}>
            {results ? 'INTELLIGENCE SECURED' : 'CONFIGURING MARKET INTELLIGENCE SENSORS'}
          </p>
        </div>

        {results ? renderResults() : renderForm()}
      </div>
    </div>
  );
};

export default LaunchMissionModal;
